import { format, parseISO } from "date-fns";
import { quoteForDate, _quotesForTests as allQuotes } from "@/lib/dailyQuote";
import { tipForDate, _tipsForTests as allTips } from "@/lib/dailyClinicalTip";

const ROLE_META = [
  { key: "psych_rounder", label: "Psych Rounders", accent: "#0284c7" },
  { key: "php", label: "PHP Cover (Psych)", accent: "#d96c4e" },
  { key: "medical_rounder", label: "Medical Rounder", accent: "#0284c7" },
  { key: "medical_php", label: "PHP Medical Intake", accent: "#d96c4e" },
  { key: "psych_on_call", label: "Psych On Call", accent: "#6b4f8e" },
  { key: "medical_on_call", label: "Medical On Call", accent: "#c2593e" },
];

function safeDate(iso) {
  try {
    return format(parseISO(iso + "T00:00:00"), "EEEE, MMMM d, yyyy");
  } catch {
    return iso;
  }
}

function CoverageCell({ label, accent, name }) {
  return (
    <div
      className="print-coverage-cell"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div
        className="print-coverage-label"
        style={{ color: accent }}
      >
        {label}
      </div>
      <div className="print-coverage-name">{name || "—"}</div>
    </div>
  );
}

function roomOf(p) {
  const r = (p.room || "").trim();
  if (r) return r;
  const m = (p.note || "").match(/Room\s+([A-Za-z0-9\-]+)/i);
  return m ? m[1] : "";
}

function noteOf(p) {
  const base = (p.note || "")
    .replace(/Room\s+[A-Za-z0-9\-]+/i, "")
    .replace(/^[\s·\-|,]+/, "")
    .trim();
  const csj = (p.continued_stay_justification || "").trim();
  if (csj && base) return `${base}  ·  CSJ: ${csj}`;
  if (csj) return `CSJ: ${csj}`;
  return base;
}

function losDays(p) {
  // Parse "(N)" trailing LOS-in-days from the raw admission paste string.
  // Falls back to computing days from the normalized admission_date so that
  // pre-existing patients (before admission_raw was tracked) still get flagged.
  const raw = (p.admission_raw || "").trim();
  const m = raw.match(/\((\d+)\)/);
  if (m) return parseInt(m[1], 10);
  const d = (p.admission_date || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const adm = new Date(d + "T00:00:00");
    if (!Number.isNaN(adm.getTime())) {
      return Math.max(0, Math.floor((Date.now() - adm.getTime()) / 86400000));
    }
  }
  return null;
}

function losTag(p) {
  const days = losDays(p);
  if (days === null) return null;
  if (days <= 3 && !p.is_new) return { text: `⚠ LOS ${days}d`, kind: "short" };
  if (days >= 14) return { text: `LOS ${days}d`, kind: "long" };
  return null;
}

export default function PrintSheet({ day, providers }) {
  if (!day) return null;
  const providerMap = Object.fromEntries(providers.map((p) => [p.id, p]));

  const cfg = (day.available_providers || []).slice().sort((a, b) => {
    const na = providerMap[a.provider_id]?.name || "";
    const nb = providerMap[b.provider_id]?.name || "";
    return na.localeCompare(nb, undefined, { sensitivity: "base" });
  });
  const psychRounderNames = cfg
    .map((c) => providerMap[c.provider_id]?.name)
    .filter(Boolean)
    .join(", ");
  const phpCover = cfg.find((c) => c.covers_php);
  const phpName = phpCover ? providerMap[phpCover.provider_id]?.name : null;
  const medRounderName = day.medical_rounder_id
    ? providerMap[day.medical_rounder_id]?.name
    : null;
  const medPhp = day.medical_rounder_covers_php ? medRounderName : null;
  const psychOnCallName = day.psych_on_call_id
    ? providerMap[day.psych_on_call_id]?.name
    : null;
  const medOnCallName = day.medical_on_call_id
    ? providerMap[day.medical_on_call_id]?.name
    : null;

  const grouped = {};
  (day.patients || []).forEach((p) => {
    const pid = p.provider_id || "__unassigned__";
    (grouped[pid] = grouped[pid] || []).push(p);
  });
  (day.unassigned || []).forEach((p) => {
    (grouped["__unassigned__"] = grouped["__unassigned__"] || []).push(p);
  });
  Object.values(grouped).forEach((arr) =>
    arr.sort(
      (a, b) =>
        (a.unit || "").localeCompare(b.unit || "") ||
        a.name.localeCompare(b.name)
    )
  );

  const values = {
    psych_rounder: psychRounderNames,
    php: phpName,
    medical_rounder: medRounderName,
    medical_php: medPhp,
    psych_on_call: psychOnCallName,
    medical_on_call: medOnCallName,
  };

  return (
    <div
      className="print-only print-sheet"
      data-print-scope="rounding"
      aria-hidden="true"
    >
      {/* Masthead */}
      <div className="print-masthead">
        <div>
          <div className="print-brand">Rounds</div>
          <div className="print-title">
            Daily Rounding List
          </div>
          <div className="print-date">{safeDate(day.date)}</div>
        </div>
        <div className="print-meta">
          <div className="print-meta-label">Total patients</div>
          <div className="print-meta-value">{(day.patients || []).length}</div>
        </div>
      </div>

      {/* Coverage strip */}
      <div className="print-coverage-grid">
        {ROLE_META.filter((r) => values[r.key]).map((r) => (
          <CoverageCell
            key={r.key}
            label={r.label}
            accent={r.accent}
            name={values[r.key]}
          />
        ))}
      </div>

      {/* Daily motivational quote + clinical tip — side-by-side compact row
          so both fit on the same one-page printout without pushing the
          rounding tables onto page two. */}
      {(() => {
        const baseQ = quoteForDate(day.date);
        const qOff = day.quote_offset || 0;
        const q = qOff
          ? allQuotes[(allQuotes.findIndex((x) => x.text === baseQ.text) + qOff + allQuotes.length) % allQuotes.length]
          : baseQ;
        const baseT = tipForDate(day.date);
        const tOff = day.tip_offset || 0;
        const t = tOff
          ? allTips[(allTips.findIndex((x) => x.text === baseT.text) + tOff + allTips.length) % allTips.length]
          : baseT;
        return (
          <div className="print-quote-tip-row">
            <div className="print-quote" data-testid="print-daily-quote">
              <div className="print-quote-mark">&ldquo;</div>
              <div className="print-quote-body">
                <div className="print-quote-text">{q.text}</div>
                <div className="print-quote-author">— {q.author}</div>
              </div>
            </div>
            <div className="print-tip" data-testid="print-clinical-tip">
              <div className="print-tip-label">Tip · {t.category}</div>
              <div className="print-tip-text">{t.text}</div>
            </div>
          </div>
        );
      })()}

      {/* Provider tables */}
      {cfg.map((c) => {
        const prov = providerMap[c.provider_id];
        if (!prov) return null;
        const list = grouped[c.provider_id] || [];
        const rt = (day.provider_rounding_times || {})[c.provider_id] || "";
        return (
          <section
            key={c.provider_id}
            className="print-provider-block"
          >
            <div className="print-provider-header">
              <div className="print-provider-name">{prov.name}</div>
              <div className="print-provider-badges">
                {c.covers_php && (
                  <span className="print-badge print-badge-php">PHP</span>
                )}
                {prov.unit_preferences?.length > 0 && (
                  <span className="print-badge print-badge-unit">
                    {prov.unit_preferences.map((u) => `U${u}`).join(" · ")}
                  </span>
                )}
                {rt && (
                  <span className="print-badge print-badge-time">
                    Rounding: {rt}
                  </span>
                )}
              </div>
              <div className="print-provider-count">
                {list.length} <span>patient{list.length !== 1 ? "s" : ""}</span>
              </div>
            </div>
            {list.length === 0 ? (
              <div className="print-empty">No patients assigned.</div>
            ) : (
              <table className="print-table">
                <colgroup>
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Room #</th>
                    <th>Admit (LOS)</th>
                    <th>Status</th>
                    <th>Projected d/c</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                {list.map((p) => {
                    const lt = losTag(p);
                    return (
                    <tr
                      key={p.id}
                      className={
                        p.is_new
                          ? "print-row-new-admit"
                          : p.is_followup
                          ? "print-row-followup"
                          : ""
                      }
                    >
                      <td className="print-row-name">
                        {p.name}
                        {p.is_new && (
                          <span className="print-new-flag"> ★ NEW</span>
                        )}
                        {p.is_followup && (
                          <span className="print-fu-flag"> · F/U</span>
                        )}
                        {lt && (
                          <span
                            className={
                              lt.kind === "short"
                                ? "print-los-flag print-los-short"
                                : "print-los-flag print-los-long"
                            }
                          >
                            {" "}· {lt.text}
                          </span>
                        )}
                      </td>
                      <td className="print-row-mono">{roomOf(p)}</td>
                      <td className="print-row-mono">{p.admission_raw || p.admission_date || ""}</td>
                      <td>
                        <span
                          className={
                            p.status === "involuntary"
                              ? "print-status-invol"
                              : "print-status-vol"
                          }
                        >
                          {p.status === "involuntary" ? "INVOL" : "VOL"}
                        </span>
                      </td>
                      <td className="print-row-mono">{p.projected_discharge || ""}</td>
                      <td className="print-row-notes">{noteOf(p)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        );
      })}

      {/* Unassigned */}
      {(grouped.__unassigned__ || []).length > 0 && (
        <section className="print-provider-block print-provider-block-unassigned">
          <div className="print-provider-header">
            <div className="print-provider-name">Unassigned</div>
            <div className="print-provider-count">
              {grouped.__unassigned__.length}{" "}
              <span>
                patient{grouped.__unassigned__.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
          <table className="print-table">
            <colgroup>
              <col style={{ width: "26%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Room #</th>
                <th>Admit (LOS)</th>
                <th>Status</th>
                <th>Projected d/c</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {grouped.__unassigned__.map((p) => (
                <tr key={p.id}>
                  <td className="print-row-name">{p.name}</td>
                  <td className="print-row-mono">{roomOf(p)}</td>
                  <td className="print-row-mono">{p.admission_raw || p.admission_date || ""}</td>
                  <td>
                    {p.status === "involuntary" ? "INVOL" : "VOL"}
                  </td>
                  <td className="print-row-mono">{p.projected_discharge || ""}</td>
                  <td className="print-row-notes">{noteOf(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Footer */}
      <div className="print-footer">
        Generated {format(new Date(), "MMM d, yyyy · h:mm a")} · Rounds
      </div>
    </div>
  );
}
